import { useState, useMemo } from 'react';
import { ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Brush, Area, Scatter } from 'recharts';

/**
 * Generic multi-profile overlay chart with:
 *   - legend-click toggle (hide/show individual profiles)
 *   - optional recharts <Brush> zoom
 *   - optional median/IQR ribbon
 *   - optional sample-point scatter overlay
 *
 * Data shape: an array of rows with one numeric x-key and one numeric value per series.
 * seriesKeys: array of {key, color} — where `key` is the column name in each row.
 *
 * Uses `opacity` to dim non-hovered series (task P3 #19). Hovered series thickens via activeDot.
 */
export default function ProfileOverlayChart({
  data,
  seriesKeys,
  xKey = 'day',
  xLabel = 'Days Since Input',
  yLabel = '',
  yFormatter,
  xFormatter,
  showBrush = false,
  showDistributionRibbon = false,
  scatterData = null,  // optional dot overlay (e.g., sample points)
  height = 400,
  testId,
}) {
  const [hidden, setHidden] = useState(new Set());
  const [hoveredKey, setHoveredKey] = useState(null);

  const toggle = (key) => {
    setHidden(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Format legend with click-to-toggle semantic
  const renderLegend = (props) => {
    const { payload } = props;
    return (
      <ul className="flex flex-wrap gap-3 px-2 py-1 text-xs" data-testid={testId ? `${testId}-legend` : undefined}>
        {payload.filter(p => !String(p.dataKey).startsWith('_')).map((entry) => {
          const isHidden = hidden.has(entry.dataKey);
          return (
            <li
              key={entry.dataKey}
              onClick={() => toggle(entry.dataKey)}
              onMouseEnter={() => setHoveredKey(entry.dataKey)}
              onMouseLeave={() => setHoveredKey(null)}
              className={`flex items-center gap-1.5 cursor-pointer select-none ${isHidden ? 'opacity-40' : 'opacity-100'} hover:bg-slate-100 rounded px-1.5 py-0.5`}
              data-testid={`legend-${entry.dataKey}`}
            >
              <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: entry.color, outline: isHidden ? '1px dashed #94a3b8' : 'none' }}
              />
              <span className={`${isHidden ? 'line-through text-slate-400' : 'text-slate-700'}`}>{entry.value}</span>
            </li>
          );
        })}
      </ul>
    );
  };

  const visibleSeriesCount = useMemo(
    () => seriesKeys.filter(s => !hidden.has(s.key)).length,
    [seriesKeys, hidden]
  );

  return (
    <div data-testid={testId}>
      <div className="text-[11px] text-slate-400 italic mb-1 px-2">
        Click a legend item to hide / show a profile.
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey={xKey}
            tickFormatter={xFormatter}
            label={{ value: xLabel, position: 'insideBottom', offset: -5, fontSize: 11, fill: '#6b7280' }}
            fontSize={11}
            stroke="#9ca3af"
          />
          <YAxis
            tickFormatter={yFormatter}
            label={{ value: yLabel, angle: -90, position: 'insideLeft', fontSize: 11, fill: '#6b7280' }}
            fontSize={11}
            stroke="#9ca3af"
          />
          <Tooltip
            contentStyle={{ fontSize: 11, borderRadius: 6 }}
            formatter={(val, name) => {
              if (String(name).startsWith('_')) return null;  // hide ribbon keys
              return [yFormatter ? yFormatter(val) : val, name];
            }}
          />
          <Legend content={renderLegend} />

          {/* Distribution ribbon — drawn first so profile lines overlay it */}
          {showDistributionRibbon && visibleSeriesCount >= 5 && (
            <>
              <Area type="monotone" dataKey="_p75" stroke="none" fill="#cbd5e1" fillOpacity={0.25} isAnimationActive={false} />
              <Area type="monotone" dataKey="_p25" stroke="none" fill="#ffffff" fillOpacity={1} isAnimationActive={false} />
              <Line type="monotone" dataKey="_median" stroke="#64748b" strokeDasharray="4 2" dot={false} strokeWidth={1.5} isAnimationActive={false} />
            </>
          )}

          {seriesKeys.map(({ key, color }) => {
            const isHidden = hidden.has(key);
            const isDimmed = hoveredKey && hoveredKey !== key;
            return (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={color}
                strokeWidth={hoveredKey === key ? 3 : 2}
                dot={false}
                activeDot={{ r: 5 }}
                hide={isHidden}
                strokeOpacity={isDimmed ? 0.25 : 1}
                isAnimationActive={false}
              />
            );
          })}

          {scatterData && (
            <Scatter data={scatterData} dataKey="weight" shape="circle">
              {/* Scatter uses its own colour prop via cell components in parent if needed */}
            </Scatter>
          )}

          {showBrush && data.length > 20 && (
            <Brush dataKey={xKey} height={18} stroke="#64748b" travellerWidth={8} />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
